"use client";

import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControlLabel, InputAdornment, Paper,
  Switch, Table, TableBody, TableCell, TableHead, TableRow, TablePagination,
  TextField, Tooltip, Typography,
} from "@mui/material";
import FlagIcon from "@mui/icons-material/Flag";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import SearchIcon from "@mui/icons-material/Search";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { axiosRequest } from "../../utility/axios";
import { apiBaseUrl } from "../../constant/endpoint";
import { getSweetAlert, maskPhone } from "../../helper";

const fmt = (n) => Number(n || 0).toLocaleString("en-IN");

export default function PlayerManager() {
  const token = useSelector((s) => s.loginSlice.token);

  const [players, setPlayers]       = useState([]);
  const [total, setTotal]           = useState(0);
  const [loading, setLoading]       = useState(false);
  const [search, setSearch]         = useState("");
  const [onlyFlagged, setOnlyFlagged] = useState(false);
  const [page, setPage]             = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Red-flag dialog
  const [flagDialog, setFlagDialog] = useState(null); // player object
  const [flagRemark, setFlagRemark] = useState("");
  const [flagSaving, setFlagSaving] = useState(false);

  // Withdraw-lock confirm dialog
  const [lockDialog, setLockDialog] = useState(null); // player object
  const [lockSaving, setLockSaving] = useState(false);

  const fetchPlayers = useCallback(async (pg = page, sz = rowsPerPage) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pg + 1,
        size: sz,
        ...(search.trim() ? { search: search.trim() } : {}),
        ...(onlyFlagged ? { red_flag: "true" } : {}),
      });
      const res = await axiosRequest("GET", `${apiBaseUrl}/players?${params}`, token);
      setPlayers(res?.data?.players || []);
      setTotal(res?.data?.total || 0);
    } finally {
      setLoading(false);
    }
  }, [search, onlyFlagged, page, rowsPerPage, token]);

  useEffect(() => { fetchPlayers(); }, []);

  const handleSearch = () => { setPage(0); fetchPlayers(0, rowsPerPage); };

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
    fetchPlayers(newPage, rowsPerPage);
  };

  const handleRowsChange = (e) => {
    const sz = parseInt(e.target.value, 10);
    setRowsPerPage(sz);
    setPage(0);
    fetchPlayers(0, sz);
  };

  const openFlagDialog = (player) => {
    setFlagRemark(player.red_flag ? player.red_flag_remark || "" : "");
    setFlagDialog(player);
  };

  const handleFlagSave = async () => {
    if (!flagDialog) return;
    const isRemoving = flagDialog.red_flag;
    if (!isRemoving && !flagRemark.trim()) {
      getSweetAlert("error", "Remark is required when flagging a player");
      return;
    }
    setFlagSaving(true);
    try {
      const res = await axiosRequest("PATCH", `${apiBaseUrl}/players/${flagDialog._id}/red-flag`, token, {
        red_flag: !isRemoving,
        red_flag_remark: flagRemark.trim(),
      });
      if (res?.data?.name || res?.status === "success") {
        getSweetAlert("success", isRemoving ? "Red flag removed" : "Player flagged");
        setFlagDialog(null);
        fetchPlayers();
      } else {
        getSweetAlert("error", res?.data?.message || "Failed");
      }
    } finally {
      setFlagSaving(false);
    }
  };

  const handleLockSave = async () => {
    if (!lockDialog) return;
    const unlocking = !!lockDialog.withdraw_lock;
    setLockSaving(true);
    try {
      const res = await axiosRequest("PATCH", `${apiBaseUrl}/players/${lockDialog._id}/withdraw-lock`, token, {
        withdraw_lock: !unlocking,
      });
      if (res?.data?.name || res?.status === "success") {
        getSweetAlert("success", unlocking ? "Withdrawal unlocked" : "Withdrawal locked");
        setLockDialog(null);
        fetchPlayers();
      } else {
        getSweetAlert("error", res?.data?.message || "Failed");
      }
    } finally {
      setLockSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, mt: { xs: 7, md: 12 }, pb: 8 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Player Manager</Typography>
          <Typography color="text.secondary">View all players and manage red flags</Typography>
        </Box>
        <Chip
          icon={<FlagIcon sx={{ fontSize: 16 }} />}
          label={`${players.filter((p) => p.red_flag).length} Flagged`}
          color="error"
          variant="outlined"
        />
      </Box>

      {/* Filters */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2, border: "1px solid #e0e0e0" }}>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <TextField
            size="small"
            placeholder="Search by name, phone, player ID"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            sx={{ minWidth: 280 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
          <FormControlLabel
            control={<Switch checked={onlyFlagged} onChange={(e) => { setOnlyFlagged(e.target.checked); setPage(0); setTimeout(() => fetchPlayers(0, rowsPerPage), 0); }} />}
            label="Only Red Flagged"
            sx={{ ml: 1 }}
          />
          <Button variant="contained" onClick={handleSearch} sx={{ ml: "auto" }}>Search</Button>
        </Box>
      </Paper>

      {/* Table */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #e0e0e0", overflow: "hidden" }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
        ) : (
          <Table size="small">
            <TableHead sx={{ background: "#1c2433" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Player</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Phone</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Player ID</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Deposit</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Winning</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Promo</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Loan</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Float</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Joined</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {players.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} align="center" sx={{ py: 6, color: "text.secondary" }}>
                    No players found
                  </TableCell>
                </TableRow>
              ) : players.map((p, i) => (
                <TableRow
                  key={p._id}
                  hover
                  sx={{ background: p.red_flag ? "rgba(211,47,47,0.04)" : undefined }}
                >
                  <TableCell>{page * rowsPerPage + i + 1}</TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {p.red_flag && (
                        <Tooltip title={p.red_flag_remark || "Red flagged"} arrow>
                          <FlagIcon sx={{ fontSize: 16, color: "error.main", flexShrink: 0 }} />
                        </Tooltip>
                      )}
                      {p.withdraw_lock && (
                        <Tooltip title="Withdrawal locked" arrow>
                          <LockIcon sx={{ fontSize: 16, color: "warning.main", flexShrink: 0 }} />
                        </Tooltip>
                      )}
                      <Box>
                        <Typography fontWeight={600} fontSize={13}>{p.name || "—"}</Typography>
                        {p.email && (
                          <Typography variant="caption" color="text.secondary">{p.email}</Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{maskPhone(p.phone) || "—"}</TableCell>
                  <TableCell>
                    <Typography fontFamily="monospace" fontSize={12}>{p.playerId || "—"}</Typography>
                  </TableCell>
                  <TableCell align="right">{fmt(p.cash_deposite)}</TableCell>
                  <TableCell align="right">{fmt(p.winning_balance)}</TableCell>
                  <TableCell align="right">{fmt(p.promotional_balance)}</TableCell>
                  <TableCell align="right">
                    {Number(p.loan_distributed || 0) > 0 ? (
                      <Typography color="error.main" fontWeight={600} fontSize={13}>
                        {fmt(p.loan_distributed)}
                      </Typography>
                    ) : <Typography color="text.disabled">—</Typography>}
                  </TableCell>
                  <TableCell align="right">
                    {Number(p.float_outstanding || 0) > 0 ? (
                      <Typography color="warning.dark" fontWeight={600} fontSize={13}>
                        {fmt(p.float_outstanding)}
                      </Typography>
                    ) : <Typography color="text.disabled">—</Typography>}
                  </TableCell>
                  <TableCell>
                    <Typography fontSize={12} color="text.secondary">
                      {p.created ? new Date(p.created).toLocaleDateString("en-IN") : "—"}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
                      {p.red_flag ? (
                        <Chip label="RED FLAG" size="small" color="error" icon={<FlagIcon sx={{ fontSize: 14 }} />} />
                      ) : (
                        <Chip label="Clear" size="small" color="success" variant="outlined" />
                      )}
                      {p.withdraw_lock && (
                        <Chip label="WITHDRAW LOCKED" size="small" color="warning" icon={<LockIcon sx={{ fontSize: 14 }} />} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" flexDirection="column" alignItems="stretch" gap={0.5}>
                    <Button
                      size="small"
                      variant={p.red_flag ? "outlined" : "contained"}
                      color="error"
                      startIcon={<FlagIcon sx={{ fontSize: 14 }} />}
                      onClick={() => openFlagDialog(p)}
                      sx={{ fontSize: 11, whiteSpace: "nowrap" }}
                    >
                      {p.red_flag ? "Remove Flag" : "Red Flag"}
                    </Button>
                    <Button
                      size="small"
                      variant={p.withdraw_lock ? "outlined" : "contained"}
                      color="warning"
                      startIcon={p.withdraw_lock ? <LockOpenIcon sx={{ fontSize: 14 }} /> : <LockIcon sx={{ fontSize: 14 }} />}
                      onClick={() => setLockDialog(p)}
                      sx={{ fontSize: 11, whiteSpace: "nowrap" }}
                    >
                      {p.withdraw_lock ? "Unlock Withdraw" : "Withdraw Lock"}
                    </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsChange}
          rowsPerPageOptions={[20, 50, 100]}
        />
      </Paper>

      {/* Red Flag Dialog */}
      <Dialog open={!!flagDialog} onClose={() => setFlagDialog(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{ background: flagDialog?.red_flag ? "linear-gradient(135deg,#37474f,#546e7a)" : "linear-gradient(135deg,#b71c1c,#d32f2f)", px: 3, py: 2, color: "#fff" }}>
          <Box display="flex" alignItems="center" gap={1}>
            <FlagIcon />
            <Typography variant="h6" fontWeight={700}>
              {flagDialog?.red_flag ? "Remove Red Flag" : "Red Flag Player"}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ opacity: 0.8 }}>
            {flagDialog?.name} — {maskPhone(flagDialog?.phone) || flagDialog?.playerId}
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 3 }}>
          {flagDialog?.red_flag ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              Current remark: <strong>{flagDialog.red_flag_remark || "—"}</strong>
            </Alert>
          ) : null}
          {!flagDialog?.red_flag && (
            <TextField
              fullWidth
              size="small"
              label="Remark *"
              placeholder="Reason for flagging this player"
              value={flagRemark}
              onChange={(e) => setFlagRemark(e.target.value)}
              multiline
              rows={3}
              required
              error={!flagRemark.trim()}
              helperText={!flagRemark.trim() ? "Remark is required" : ""}
            />
          )}
          {flagDialog?.red_flag && (
            <Typography variant="body2" color="text.secondary">
              This will remove the red flag and clear the remark for this player.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button variant="outlined" onClick={() => setFlagDialog(null)} disabled={flagSaving}>Cancel</Button>
          <Button
            variant="contained"
            color={flagDialog?.red_flag ? "inherit" : "error"}
            onClick={handleFlagSave}
            disabled={flagSaving || (!flagDialog?.red_flag && !flagRemark.trim())}
          >
            {flagSaving ? "Saving…" : flagDialog?.red_flag ? "Remove Flag" : "Confirm Flag"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Withdraw Lock Dialog */}
      <Dialog open={!!lockDialog} onClose={() => setLockDialog(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{ background: lockDialog?.withdraw_lock ? "linear-gradient(135deg,#37474f,#546e7a)" : "linear-gradient(135deg,#e65100,#f57c00)", px: 3, py: 2, color: "#fff" }}>
          <Box display="flex" alignItems="center" gap={1}>
            {lockDialog?.withdraw_lock ? <LockOpenIcon /> : <LockIcon />}
            <Typography variant="h6" fontWeight={700}>
              {lockDialog?.withdraw_lock ? "Unlock Withdrawal" : "Lock Withdrawal"}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ opacity: 0.8 }}>
            {lockDialog?.name} — {maskPhone(lockDialog?.phone) || lockDialog?.playerId}
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2" color="text.secondary">
            {lockDialog?.withdraw_lock
              ? "This player will be able to withdraw again."
              : "This player will not be able to withdraw any amount — from the CMS or the Telegram bot — until unlocked."}
          </Typography>
          {lockDialog?.withdraw_lock && lockDialog?.withdraw_lock_at && (
            <Typography variant="caption" color="text.secondary" display="block" mt={1}>
              Locked since {new Date(lockDialog.withdraw_lock_at).toLocaleString("en-IN")}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button variant="outlined" onClick={() => setLockDialog(null)} disabled={lockSaving}>Cancel</Button>
          <Button
            variant="contained"
            color={lockDialog?.withdraw_lock ? "inherit" : "warning"}
            onClick={handleLockSave}
            disabled={lockSaving}
          >
            {lockSaving ? "Saving…" : lockDialog?.withdraw_lock ? "Unlock" : "Confirm Lock"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
